import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAccountingMode } from '../contexts/AccountingModeContext';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Layers, Mountain, Building2, Package, Wallet, Receipt } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR').format(Math.round(amount || 0)) + ' FCFA';

const AnalyticsPage = () => {
  const { getAuthHeaders } = useAuth();
  const { mode } = useAccountingMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchAnalytics();
  }, [mode, selectedMonth, selectedYear]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/analytics/margins?month=${selectedMonth}&year=${selectedYear}&accounting_mode=${mode}`,
        getAuthHeaders()
      );
      setData(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  if (!data) {
    return <div className="flex items-center justify-center h-full text-slate-500">Aucune donnée disponible</div>;
  }

  const { agglos, granulats, total, expenses } = data;

  // Chart data for agglos
  const aggloChartData = agglos.products.map(p => ({
    name: p.name,
    'Coût Production': p.production_cost,
    'Prix Catalogue': p.catalog_price,
    'Marge Unitaire': p.margin_per_unit
  }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <BarChart3 className="w-10 h-10" />
            Analyses et Marges
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Analyse des marges basée sur les prix du catalogue
            {mode === 'total' && <span className="ml-2 text-[#FDC800] font-bold">(Vue Totale A+B)</span>}
            {mode === 'declared' && <span className="ml-2 text-[#1B3268] font-bold">(Vue Déclarée A)</span>}
          </p>
        </div>
      </div>

      {/* Month/Year Selector */}
      <Card className="p-4 bg-white border border-slate-200 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700">Mois :</Label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="analytics-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700">Année :</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="analytics-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-500 ml-auto font-medium">
            {MONTHS[selectedMonth - 1]} {selectedYear}
          </div>
        </div>
      </Card>

      {/* KPI Cards - Total AGRABAT */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="total-kpi-cards">
        <Card className="bg-green-50 border border-green-200 rounded-sm p-5">
          <p className="text-xs font-medium text-green-700 uppercase tracking-wider">CA Total</p>
          <p className="text-xl font-bold text-green-800 mt-1">{formatCurrency(total.revenue)}</p>
        </Card>
        <Card className="bg-red-50 border border-red-200 rounded-sm p-5">
          <p className="text-xs font-medium text-red-700 uppercase tracking-wider">Coûts Production</p>
          <p className="text-xl font-bold text-red-800 mt-1">{formatCurrency(total.cost)}</p>
        </Card>
        <Card className="bg-gradient-to-br from-[#1B3268] to-[#2a4785] text-white border-0 rounded-sm p-5">
          <p className="text-xs text-white/70 uppercase tracking-wider">Marge Brute</p>
          <p className="text-xl font-bold mt-1">{formatCurrency(total.margin)}</p>
        </Card>
        <Card className="bg-orange-50 border border-orange-200 rounded-sm p-5" data-testid="kpi-expenses">
          <p className="text-xs font-medium text-orange-700 uppercase tracking-wider">Dépenses du Mois</p>
          <p className="text-xl font-bold text-orange-800 mt-1">{formatCurrency(expenses?.total || 0)}</p>
        </Card>
        <Card className="bg-purple-50 border border-purple-200 rounded-sm p-5" data-testid="kpi-unsold-stock">
          <p className="text-xs font-medium text-purple-700 uppercase tracking-wider">Stock Non Vendu</p>
          <p className="text-xl font-bold text-purple-800 mt-1">{formatCurrency(total.unsold_stock_value || 0)}</p>
        </Card>
        <Card className={`rounded-sm p-5 ${(total.net_profit || 0) >= 0 ? 'bg-emerald-50 border border-emerald-300' : 'bg-red-50 border border-red-300'}`} data-testid="kpi-net-profit">
          <p className={`text-xs font-medium uppercase tracking-wider ${(total.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Bénéfice Net</p>
          <p className={`text-xl font-bold mt-1 ${(total.net_profit || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{formatCurrency(total.net_profit || 0)}</p>
        </Card>
      </div>

      {/* Tabs: Agglos / Granulats / Total */}
      <Tabs defaultValue="agglos" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="agglos" className="flex items-center gap-2" data-testid="tab-agglos">
            <Layers className="w-4 h-4" />
            Agglos
          </TabsTrigger>
          <TabsTrigger value="granulats" className="flex items-center gap-2" data-testid="tab-granulats">
            <Mountain className="w-4 h-4" />
            Granulats
          </TabsTrigger>
          <TabsTrigger value="total" className="flex items-center gap-2" data-testid="tab-total">
            <Building2 className="w-4 h-4" />
            Total AGRABAT
          </TabsTrigger>
        </TabsList>

        {/* ====== AGGLOS TAB ====== */}
        <TabsContent value="agglos">
          {/* Agglo KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-blue-50 border border-blue-200 rounded-sm p-5">
              <p className="text-sm text-blue-700 font-medium">CA Agglos</p>
              <p className="text-xl font-bold text-blue-800">{formatCurrency(agglos.total_revenue)}</p>
            </Card>
            <Card className="bg-slate-50 border border-slate-200 rounded-sm p-5">
              <p className="text-sm text-slate-700 font-medium">Coût de Production</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(agglos.total_cost)}</p>
            </Card>
            <Card className={`rounded-sm p-5 ${agglos.total_margin >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-medium ${agglos.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>Marge Agglos</p>
              <p className={`text-xl font-bold ${agglos.total_margin >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(agglos.total_margin)}</p>
            </Card>
            <Card className="bg-indigo-50 border border-indigo-200 rounded-sm p-5" data-testid="agglo-produced-month">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-indigo-600" />
                <p className="text-sm text-indigo-700 font-medium">Produits (mois)</p>
              </div>
              <p className="text-xl font-bold text-indigo-800">{agglos.total_produced_month || 0} unités</p>
              <p className="text-xs text-indigo-500 mt-1">Vendus : {agglos.total_sold || 0}</p>
            </Card>
            <Card className="bg-purple-50 border border-purple-200 rounded-sm p-5" data-testid="agglo-unsold-stock">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-purple-600" />
                <p className="text-sm text-purple-700 font-medium">CA Non Vendu</p>
              </div>
              <p className="text-xl font-bold text-purple-800">{formatCurrency(agglos.total_unsold_value || 0)}</p>
              <p className="text-xs text-purple-500 mt-1">{Math.max(0, (agglos.total_produced_month || 0) - (agglos.total_sold || 0))} unités en stock</p>
            </Card>
          </div>

          {/* Prix Catalogue Agglos */}
          {agglos.catalog && agglos.catalog.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6 overflow-hidden">
              <div className="border-b border-slate-100 p-4 bg-[#FDC800]/10">
                <h3 className="text-lg font-semibold text-[#1B3268]">Prix Catalogue (Produits & Services)</h3>
              </div>
              <div className="p-4 flex flex-wrap gap-4">
                {agglos.catalog.map((item, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 min-w-[160px]">
                    <p className="text-sm font-medium text-slate-700">{item.name}</p>
                    <p className="text-lg font-bold text-[#1B3268]">{formatCurrency(item.price)}<span className="text-xs text-slate-500 ml-1">/{item.unit}</span></p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Agglo Chart */}
          {aggloChartData.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6">
              <div className="border-b border-slate-100 p-4 bg-slate-50/50">
                <h3 className="text-lg font-semibold text-[#1B3268]">Coût de Production vs Prix Catalogue</h3>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={aggloChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: '12px', fontWeight: 600 }} />
                    <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }} />
                    <Legend wrapperStyle={{ fontSize: '13px', fontWeight: 500 }} />
                    <Bar dataKey="Coût Production" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Prix Catalogue" fill="#1B3268" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Marge Unitaire" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Agglo Table */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 p-4 bg-slate-50/50">
              <h3 className="text-lg font-semibold text-[#1B3268]">Détail des Marges Agglos</h3>
              <p className="text-xs text-slate-500 mt-1">Marge = Prix Catalogue - Coût de Revient</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                    <th className="p-4 text-left">Produit</th>
                    <th className="p-4 text-right">Prix Catalogue</th>
                    <th className="p-4 text-right">Coût Revient</th>
                    <th className="p-4 text-right">Marge/Unité</th>
                    <th className="p-4 text-right">Produits (mois)</th>
                    <th className="p-4 text-right">Vendus</th>
                    <th className="p-4 text-right">Non Vendus</th>
                    <th className="p-4 text-right">CA Vendu</th>
                    <th className="p-4 text-right">CA Non Vendu</th>
                  </tr>
                </thead>
                <tbody>
                  {agglos.products.map((item) => (
                    <tr key={item.type} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`agglo-row-${item.type}`}>
                      <td className="p-4 font-medium text-[#1B3268]">{item.name}</td>
                      <td className="p-4 text-right font-mono text-blue-700">{formatCurrency(item.catalog_price)}</td>
                      <td className="p-4 text-right font-mono text-slate-600">{formatCurrency(item.production_cost)}</td>
                      <td className="p-4 text-right font-mono font-bold">
                        <span className={item.margin_per_unit >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(item.margin_per_unit)}</span>
                      </td>
                      <td className="p-4 text-right font-mono">{item.quantity_produced_month || 0}</td>
                      <td className="p-4 text-right font-mono">{item.quantity_sold}</td>
                      <td className="p-4 text-right font-mono font-bold text-purple-700">{item.unsold_qty || 0}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(item.revenue)}</td>
                      <td className="p-4 text-right font-mono font-bold text-purple-700">{formatCurrency(item.unsold_value || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {agglos.products.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune donnée de production/vente d'agglos pour {MONTHS[selectedMonth - 1]} {selectedYear}</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* ====== GRANULATS TAB ====== */}
        <TabsContent value="granulats">
          {/* Granulat KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-amber-50 border border-amber-200 rounded-sm p-5">
              <p className="text-sm text-amber-700 font-medium">CA Granulats</p>
              <p className="text-2xl font-bold text-amber-800">{formatCurrency(granulats.total_revenue)}</p>
            </Card>
            <Card className="bg-slate-50 border border-slate-200 rounded-sm p-5">
              <p className="text-sm text-slate-700 font-medium">Coût d'Achat</p>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(granulats.total_cost)}</p>
            </Card>
            <Card className={`rounded-sm p-5 ${granulats.total_margin >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-medium ${granulats.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>Marge Granulats</p>
              <p className={`text-2xl font-bold ${granulats.total_margin >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(granulats.total_margin)}</p>
            </Card>
          </div>

          {/* Prix Catalogue Granulats */}
          {granulats.catalog && granulats.catalog.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6 overflow-hidden">
              <div className="border-b border-slate-100 p-4 bg-[#FDC800]/10">
                <h3 className="text-lg font-semibold text-[#1B3268]">Prix Catalogue Granulats</h3>
              </div>
              <div className="p-4 flex flex-wrap gap-4">
                {granulats.catalog.map((item, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 min-w-[160px]">
                    <p className="text-sm font-medium text-slate-700">{item.name}</p>
                    <p className="text-lg font-bold text-[#1B3268]">{formatCurrency(item.price)}<span className="text-xs text-slate-500 ml-1">/{item.unit}</span></p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Granulat Sales Table */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 p-4 bg-slate-50/50">
              <h3 className="text-lg font-semibold text-[#1B3268]">Ventes de Granulats</h3>
              <p className="text-xs text-slate-500 mt-1">Marge = CA - Coût d'Achat</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                    <th className="p-4 text-left">Référence</th>
                    <th className="p-4 text-left">Client</th>
                    <th className="p-4 text-left">Date</th>
                    <th className="p-4 text-right">CA</th>
                    <th className="p-4 text-right">Coût d'Achat</th>
                    <th className="p-4 text-right">Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {granulats.sales.map((sale, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`granulat-sale-${idx}`}>
                      <td className="p-4 font-medium text-[#1B3268]">{sale.reference}</td>
                      <td className="p-4">{sale.client}</td>
                      <td className="p-4 text-sm text-slate-600">{sale.date ? new Date(sale.date).toLocaleDateString('fr-FR') : '-'}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(sale.revenue)}</td>
                      <td className="p-4 text-right font-mono text-slate-600">{formatCurrency(sale.cost)}</td>
                      <td className="p-4 text-right font-mono font-bold">
                        <span className={sale.margin >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(sale.margin)}</span>
                      </td>
                    </tr>
                  ))}
                  {granulats.sales.length > 0 && (
                    <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
                      <td className="p-4" colSpan={3}>TOTAL</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(granulats.total_revenue)}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(granulats.total_cost)}</td>
                      <td className="p-4 text-right font-mono">
                        <span className={granulats.total_margin >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(granulats.total_margin)}</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {granulats.sales.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Mountain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune vente de granulats pour {MONTHS[selectedMonth - 1]} {selectedYear}</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* ====== TOTAL AGRABAT TAB ====== */}
        <TabsContent value="total">
          <div className="space-y-6">
            {/* Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Agglos Summary */}
              <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                <div className="border-b border-blue-100 p-4 bg-blue-50">
                  <h3 className="text-lg font-semibold text-[#1B3268] flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Agglos
                  </h3>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Chiffre d'Affaires</span>
                    <span className="font-mono font-bold">{formatCurrency(agglos.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Coût de Production</span>
                    <span className="font-mono text-slate-700">{formatCurrency(agglos.total_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-purple-600">CA Non Vendu (Stock)</span>
                    <span className="font-mono font-bold text-purple-700">{formatCurrency(agglos.total_unsold_value || 0)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-sm font-medium text-slate-800">Marge</span>
                    <span className={`font-mono font-bold ${agglos.total_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(agglos.total_margin)}</span>
                  </div>
                </div>
              </Card>

              {/* Granulats Summary */}
              <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                <div className="border-b border-amber-100 p-4 bg-amber-50">
                  <h3 className="text-lg font-semibold text-[#1B3268] flex items-center gap-2">
                    <Mountain className="w-5 h-5" />
                    Granulats
                  </h3>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Chiffre d'Affaires</span>
                    <span className="font-mono font-bold">{formatCurrency(granulats.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Coût d'Achat</span>
                    <span className="font-mono text-slate-700">{formatCurrency(granulats.total_cost)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-sm font-medium text-slate-800">Marge</span>
                    <span className={`font-mono font-bold ${granulats.total_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(granulats.total_margin)}</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Expenses Card */}
            <Card className="bg-white border border-orange-200 rounded-sm shadow-sm overflow-hidden" data-testid="expenses-card">
              <div className="border-b border-orange-100 p-4 bg-orange-50">
                <h3 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Dépenses du Mois — {MONTHS[selectedMonth - 1]} {selectedYear}
                </h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Charges Fixes</p>
                    <p className="text-xl font-bold text-orange-700 mt-1">{formatCurrency(expenses?.fixed || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Charges Variables</p>
                    <p className="text-xl font-bold text-orange-700 mt-1">{formatCurrency(expenses?.variable || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Salaires</p>
                    <p className="text-xl font-bold text-blue-700 mt-1" data-testid="expenses-salaries">{formatCurrency(expenses?.salaries || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Transport Agglos</p>
                    <p className="text-xl font-bold text-orange-600 mt-1" data-testid="expenses-transport">{formatCurrency(expenses?.transport || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Chargement</p>
                    <p className="text-xl font-bold text-orange-600 mt-1" data-testid="expenses-loading">{formatCurrency(expenses?.loading || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Dépenses</p>
                    <p className="text-xl font-bold text-orange-800 mt-1">{formatCurrency(expenses?.total || 0)}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Total AGRABAT - Net Profit */}
            <Card className="bg-gradient-to-br from-[#1B3268] to-[#2a4785] text-white border-0 rounded-sm shadow-lg overflow-hidden" data-testid="total-agrabat-card">
              <div className="p-6">
                <h3 className="text-xl font-bold flex items-center gap-2 mb-4">
                  <Building2 className="w-6 h-6" />
                  Total AGRABAT — {MONTHS[selectedMonth - 1]} {selectedYear}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">CA Total</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(total.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Coûts Prod.</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(total.cost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Marge Brute</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(total.margin)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Dépenses</p>
                    <p className="text-lg font-bold mt-1 text-orange-300">{formatCurrency(total.expenses || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Stock Non Vendu</p>
                    <p className="text-lg font-bold mt-1 text-purple-300">{formatCurrency(total.unsold_stock_value || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Bénéfice Net</p>
                    <p className={`text-xl font-bold mt-1 ${(total.net_profit || 0) >= 0 ? 'text-[#FDC800]' : 'text-red-400'}`}>{formatCurrency(total.net_profit || 0)}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Bar Chart Comparison */}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="border-b border-slate-100 p-4 bg-slate-50/50">
                <h3 className="text-lg font-semibold text-[#1B3268]">Comparaison Agglos vs Granulats</h3>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={[
                    { name: 'Agglos', CA: agglos.total_revenue, 'Coûts': agglos.total_cost, Marge: agglos.total_margin },
                    { name: 'Granulats', CA: granulats.total_revenue, 'Coûts': granulats.total_cost, Marge: granulats.total_margin },
                    { name: 'Total', CA: total.revenue, 'Coûts': total.cost, Marge: total.margin }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: '12px', fontWeight: 600 }} />
                    <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }} />
                    <Legend wrapperStyle={{ fontSize: '13px', fontWeight: 500 }} />
                    <Bar dataKey="CA" fill="#1B3268" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Coûts" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Marge" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
